import React from 'react';
import { FormattedMessage, FormattedDate } from 'react-intl';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Avatar } from '@companycam/slab-web';
import { splitTokenizedText } from './helpers/tokenutils';
import { useCurrentCompany, useAvatarData } from 'javascripts/hooks';

const boldableText = ({ key, text, isBold }) =>
  isBold ? <b key={key}>{text}</b> : <span key={key}>{text}</span>;

const getFormattedComment = ({ id, comment, metadata }) => {
  const tokens = metadata ? metadata.mentions || metadata.users || [] : [];

  if (tokens.length === 0) {
    return boldableText({
      key: `${id}-1`,
      text: comment,
      isBold: false,
    });
  }

  return splitTokenizedText(comment, tokens).map((chunk) =>
    boldableText({
      key: `${id}-${chunk.start}`,
      text: chunk.text,
      isBold: !!chunk.value,
    }),
  );
};

const Comment = ({ isCurrentUser, comment }) => {
  const { contributor } = comment;
  const avatarData = useAvatarData(comment);
  const { currentCompany } = useCurrentCompany();
  const isGuest = contributor.__typename === 'ProjectContributingGuest';
  const isDifferentCompany = currentCompany.node_id !== contributor.companyId;
  const commentClasses = cx('single-comment', {
    'is-current-user': isCurrentUser,
    'external-user': isGuest,
  });
  const formattedComment = getFormattedComment(comment);
  let creatorDisplayName = contributor.name || 'Unknown User';
  if (isDifferentCompany && contributor.companyName) {
    creatorDisplayName = `${creatorDisplayName} (${contributor.companyName})`;
  }

  return (
    <div className={commentClasses}>
      <Avatar
        fullName={avatarData.name}
        imageSrc={avatarData.src}
        size="medium"
        borderColor={isDifferentCompany ? avatarData.accentColor : undefined}
      />
      <div className="comment-details">
        <div className="commentor-name">
          {isGuest && <i className="mdi mdi-account-box guest" />}
          <span>{creatorDisplayName}</span>
        </div>
        <div className="commentor-comment">{formattedComment}</div>
        {isGuest && (
          <span className="comment-subnote">
            <FormattedMessage
              id="Activity.comment.subnote"
              defaultMessage="Guest User cannot see replies."
              description="message to the user"
            />
          </span>
        )}
        <div className="comment-date-time">
          <FormattedDate
            value={new Date(comment.createdAt)}
            year="numeric"
            month="numeric"
            day="numeric"
            hour="numeric"
            minute="2-digit"
          />
        </div>
      </div>
    </div>
  );
};

Comment.propTypes = {
  isCurrentUser: PropTypes.bool,
  comment: PropTypes.object, // CommentFrament
};

export default Comment;
